/**
 * Админ-форма «Состав населения» (FE-10, стиль FE-15).
 *
 * RBAC (UX-слой; server enforce-ит assert_kato_access из BE-10):
 *  - super-admin (`kato_access === [0]`) — input для КАТО как text (любой код);
 *  - regular user — select из своих kato_access;
 *  - неаутентифицированный — форма не рендерится.
 *
 * Поведение:
 *  - POST на /api/supervisor/population-nationality;
 *  - после успешного create — сохраняем последний созданный id и
 *    предлагаем PUT (если юзер исправит цифры) или DELETE.
 *
 * Ограничение: GET-list endpoint отсутствует в BE-10, поэтому редактировать
 * можно только запись, ТОЛЬКО ЧТО созданную в этой сессии. Существующие
 * записи (из seed dev / прода) видны через /api/public/general
 * (latest nationality), но без id — править нельзя. Расширение —
 * отдельной задачей: BE-10-extended (GET /supervisor/population-nationality?kato=).
 */
import { Trash2 } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { useUser } from '../../../contexts/UserContext';
import {
  createNationality,
  deleteNationality,
  updateNationality,
  type NationalityResponse,
  type NationalityWriteRequest,
} from '../../../requests/supervisor.request';

interface FormSnapshot {
  kato: string;
  year: string;
  kazakhs: string;
  russians: string;
  others: string;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; record: NationalityResponse }
  | { kind: 'error'; message: string };

const parseNumberOrNull = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : null;
};

type NatKey = 'kazakhs' | 'russians' | 'others';

interface NatRow {
  key: NatKey;
  labelKey: string;
  value: string;
  setValue: (v: string) => void;
}

export const PopulationNationalityAdmin = () => {
  const { t } = useTranslation();
  const { user } = useUser();

  const [kato, setKato] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [kazakhs, setKazakhs] = useState('');
  const [russians, setRussians] = useState('');
  const [others, setOthers] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const snapshotRef = useRef<FormSnapshot>({
    kato: '',
    year: String(new Date().getFullYear()),
    kazakhs: '',
    russians: '',
    others: '',
  });

  const hasChanges =
    kato !== snapshotRef.current.kato ||
    year !== snapshotRef.current.year ||
    kazakhs !== snapshotRef.current.kazakhs ||
    russians !== snapshotRef.current.russians ||
    others !== snapshotRef.current.others;

  if (!user) {
    return (
      <p data-testid="admin-auth-required">{t('common.loading')}</p>
    );
  }

  const isSuperAdmin = user.is_admin;
  const availableKatos = isSuperAdmin ? [] : user.kato_access;

  const resetForm = () => {
    const snap = snapshotRef.current;
    setKato(snap.kato);
    setYear(snap.year);
    setKazakhs(snap.kazakhs);
    setRussians(snap.russians);
    setOthers(snap.others);
  };

  const buildBody = (): NationalityWriteRequest | null => {
    const katoNum = parseNumberOrNull(kato);
    const yearNum = parseNumberOrNull(year);
    if (katoNum === null || katoNum <= 0 || yearNum === null) return null;
    return {
      kato: katoNum,
      year: yearNum,
      kazakhs: parseNumberOrNull(kazakhs),
      russians: parseNumberOrNull(russians),
      others: parseNumberOrNull(others),
    };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const body = buildBody();
    if (!body) {
      setStatus({ kind: 'error', message: t('adminNationality.invalidInput') });
      return;
    }
    setStatus({ kind: 'submitting' });
    try {
      if (status.kind === 'success' && status.record.kato === body.kato) {
        // Если последняя успешная запись — за тот же КАТО, делаем UPDATE.
        const updated = await updateNationality(status.record.id, body);
        setStatus({ kind: 'success', record: updated });
        snapshotRef.current = { kato, year, kazakhs, russians, others };
        toast.success(t('adminNationality.saved'));
      } else {
        const created = await createNationality(body);
        setStatus({ kind: 'success', record: created });
        snapshotRef.current = { kato, year, kazakhs: '', russians: '', others: '' };
        setKazakhs('');
        setRussians('');
        setOthers('');
        toast.success(t('adminNationality.saved'));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setStatus({ kind: 'error', message });
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (status.kind !== 'success') return;
    const id = status.record.id;
    setStatus({ kind: 'submitting' });
    try {
      await deleteNationality(id);
      setStatus({ kind: 'idle' });
      snapshotRef.current = { kato: '', year: String(new Date().getFullYear()), kazakhs: '', russians: '', others: '' };
      resetForm();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setStatus({ kind: 'error', message });
      toast.error(message);
    }
  };

  const isSubmitting = status.kind === 'submitting';
  const isSuccess = status.kind === 'success';

  const natRows: NatRow[] = [
    { key: 'kazakhs', labelKey: 'adminNationality.kazakhs', value: kazakhs, setValue: setKazakhs },
    { key: 'russians', labelKey: 'adminNationality.russians', value: russians, setValue: setRussians },
    { key: 'others', labelKey: 'adminNationality.others', value: others, setValue: setOthers },
  ];

  return (
    <div className="max-w-3xl space-y-4 p-5" data-testid="population-nationality-admin">
      <div>
        <h1 className="text-2xl font-semibold">{t('adminNationality.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('adminNationality.subtitle')}</p>
      </div>

      <Card className="shadow-card">
        <CardContent className="space-y-6 pt-6">
          <form onSubmit={handleSubmit} data-testid="nationality-form">
            {/* Header filters: KATO + Year */}
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="kato">{t('adminNationality.kato')}</Label>
                {isSuperAdmin ? (
                  <Input
                    id="kato"
                    type="number"
                    value={kato}
                    onChange={(e) => setKato(e.target.value)}
                    data-testid="kato-input"
                    required
                  />
                ) : (
                  <select
                    id="kato"
                    value={kato}
                    onChange={(e) => setKato(e.target.value)}
                    data-testid="kato-select"
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">—</option>
                    {availableKatos.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <Label htmlFor="year">{t('adminNationality.year')}</Label>
                <Input
                  id="year"
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  data-testid="year-input"
                  required
                />
              </div>
            </div>

            {/* Nationality rows table */}
            {isSubmitting && !isSuccess ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adminNationality.nation')}</TableHead>
                    <TableHead className="text-right">{t('adminNationality.value')}</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {natRows.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell className="font-medium">{t(r.labelKey)}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="0"
                          className="ml-auto max-w-32 text-right tabular-nums"
                          value={r.value}
                          onChange={(e) => r.setValue(e.target.value)}
                          data-testid={`${r.key}-input`}
                        />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Status messages */}
            {status.kind === 'submitting' && (
              <p data-testid="form-submitting" className="sr-only">{t('common.loading')}</p>
            )}
            {status.kind === 'success' && (
              <p data-testid="form-success" className="text-xs text-muted-foreground">
                {t('adminNationality.saved')} (id={status.record.id})
              </p>
            )}
            {status.kind === 'error' && (
              <p data-testid="form-error" className="text-xs text-status-danger">
                {status.message}
              </p>
            )}

            {/* Action bar */}
            <div className="flex items-center justify-between border-t border-border pt-4">
              <div>
                {isSuccess && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('adminNationality.delete')}
                        data-testid="delete-button"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('adminNationality.confirmDelete')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('adminNationality.kato')}: {status.record.kato}, {t('adminNationality.year')}: {status.record.year}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>
                          {t('common.delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetForm}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !hasChanges}
                  data-testid="submit-button"
                >
                  {isSuccess
                    ? t('adminNationality.update')
                    : t('adminNationality.create')}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
